import React from 'react';
import { Layer, Shape } from 'react-konva';

/**
 * Renders the square grid lines + map background.
 * All coordinates are in *pixel* space (gridX * CELL_SIZE).
 * gridW / gridH are passed as props (from MapContext via MapCanvas).
 *
 * When `bare` is true, returns the shapes only (parent must supply a Layer).
 * Used by MapCanvas to merge multiple "layer" components into a single Konva
 * canvas, which iOS Safari composites far better than the default 10+.
 */
export const CELL_SIZE = 50;
export const GRID_W    = 30;
export const GRID_H    = 30;

export default function GridLayer({ gridW = GRID_W, gridH = GRID_H, bare = false }) {
  const width  = gridW * CELL_SIZE;
  const height = gridH * CELL_SIZE;

  // One Shape draws everything (background + all grid lines) in a single
  // canvas pass — way cheaper than ~60 React-Konva Line nodes per render.
  const sceneFunc = (ctx, shape) => {
    // Background
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    ctx.strokeStyle = '#2a2a4a';

    // Inner lines first (thin)
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    for (let x = 1; x < gridW; x++) {
      const px = x * CELL_SIZE;
      ctx.moveTo(px, 0);
      ctx.lineTo(px, height);
    }
    for (let y = 1; y < gridH; y++) {
      const py = y * CELL_SIZE;
      ctx.moveTo(0, py);
      ctx.lineTo(width, py);
    }
    ctx.stroke();

    // Outer border (thicker)
    ctx.lineWidth = 1.5;
    ctx.strokeRect(0, 0, width, height);

    // Konva contract — let the shape finish itself
    ctx.fillStrokeShape(shape);
  };

  const shape = (
    <Shape
      sceneFunc={sceneFunc}
      perfectDrawEnabled={false}
      listening={false}
      x={0} y={0}
      width={width} height={height}
    />
  );

  if (bare) return shape;
  return <Layer listening={false}>{shape}</Layer>;
}

/** Snap a pixel value to the nearest grid cell origin. */
export function snapToGrid(px) {
  return Math.round(px / CELL_SIZE) * CELL_SIZE;
}

/** Convert pixel coords to grid coords. */
export function pixelToGrid(px, py) {
  return {
    x: Math.floor(px / CELL_SIZE),
    y: Math.floor(py / CELL_SIZE),
  };
}

/** Convert grid coords to pixel coords (top-left of cell). */
export function gridToPixel(gx, gy) {
  return { x: gx * CELL_SIZE, y: gy * CELL_SIZE };
}
